import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    useGetCurrentUserQuery,
    useLazyGetCurrentUserQuery,
} from "../api/userApi";
import { setUser } from "../../../app/store/userSlice";
import { useTokenRefresh } from "../../auth/hooks/useTokenRefresh";
import { useAuth } from "../../auth/hooks/useAuth";
import { setAuthSucceeded } from "../../../app/store/authSlice";
import { reportClientError } from "../../../app/utils/errorHandling";


export const useUser = () => {
    const dispatch = useDispatch();
    const user = useSelector(state => state.user.user);
    const { refreshToken } = useTokenRefresh();
    const [hasTriedRefresh, setHasTriedRefresh] = useState(false);
    const [hasTriedLogin, setHasTriedLogin] = useState(false);
    const { loginWithTelegram } = useAuth();
    const [triggerCurrentUser] = useLazyGetCurrentUserQuery();

    const {
        data: currentUser,
        error,
    } = useGetCurrentUserQuery(undefined, {
        refetchOnMountOrArgChange: true,
        skip: !!user,
        suspense: true,
    });

    const refetchUser = useCallback(
        () => triggerCurrentUser(undefined, false),
        [triggerCurrentUser]
    );

    useEffect(() => {
        if (error && 'status' in error) {
            if (error.status === 401 && !hasTriedRefresh) {
                setHasTriedRefresh(true);
                const handleRefresh = async () => {
                    const refreshed = await refreshToken();
                    if (refreshed) {
                        try {
                            await refetchUser();
                        } catch (err) {
                            reportClientError(err, 'Failed to refetch user after refresh');
                        }
                    } else if (!hasTriedLogin && window?.Telegram?.WebApp?.initData) {
                        setHasTriedLogin(true);
                        await loginWithTelegram();
                        await refetchUser();
                    }
                };
                handleRefresh();
            }
            else if ((error.status === 403 || error.status === 404) && !hasTriedLogin && window?.Telegram?.WebApp?.initData) {
                const handleLogin = async () => {
                    if (!hasTriedLogin) {
                        setHasTriedLogin(true);
                        await loginWithTelegram();
                        await refetchUser();
                    }
                };
                handleLogin();
            }
        }
    }, [error, refreshToken, refetchUser, hasTriedRefresh, loginWithTelegram, hasTriedLogin]);

    useEffect(() => {
        if (currentUser) {
            setHasTriedRefresh(false);
            dispatch(setUser(currentUser));
            dispatch(setAuthSucceeded());
        }
    }, [currentUser, dispatch]);

    return {
        refetchUser,
        error,
        currentUser,
    };
};
